/**
 * Product variant selector with universal sizing support
 * Manages variant selection, updates prices, and syncs with sticky forms
 */

export class VariantSelector extends HTMLElement {
  private container: HTMLDivElement
  private select: HTMLSelectElement | null
  private dropdownButton: HTMLElement | null
  private sizeLabel: HTMLElement | null
  private options: NodeListOf<HTMLElement>
  private urlSync: boolean
  private pairFormId: string | null
  private priceSelector: string | null

  private static SELECTED_BUTTON_CLASSES = ['active']

  constructor() {
    super()
    // Create internal container element
    this.container = document.createElement('div')
    // Move all children to the container
    while (this.firstChild) {
      this.container.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to container
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.container.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.container)

    this.select = null
    this.dropdownButton = null
    this.sizeLabel = null
    this.urlSync = false
    this.pairFormId = null
    this.priceSelector = null
    this.options = this.container.querySelectorAll('[data-select-option]')
  }

  connectedCallback(): void {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize())
    } else {
      this.initialize()
    }
  }

  private initialize(): void {
    this.urlSync = this.hasAttribute('data-url-sync')
    this.pairFormId = this.getAttribute('data-pair-form-id')
    this.priceSelector = this.getAttribute('data-price-selector')
    this.select = this.parentNode ? this.parentNode.querySelector<HTMLSelectElement>('select[name="id"]') : null
    this.dropdownButton = this.container.querySelector<HTMLElement>('[data-modal-role="toggle"]')
    this.sizeLabel = this.dropdownButton?.querySelector<HTMLElement>('[size-selector-label') || null
    this.options = this.container.querySelectorAll<HTMLElement>('[data-select-option]')

    // Set up event listeners
    this.setupEventListeners()

    // Initialize size property if variant is already selected
    if (this.select && this.select.value) {
      const selectedOption = this.container.querySelector<HTMLElement>(`[data-variant-id="${this.select.value}"]`)
      if (selectedOption) {
        this.updateSizeProperty(selectedOption)
      }
    }
  }

  private updatePriceDisplay(option: HTMLElement): void {
    if (this.priceSelector) {
      const priceElement = document.getElementById(this.priceSelector)
      if (priceElement) {
        const priceCurrent = option.dataset.variantPrice || ''
        const priceCompare = option.dataset.variantPriceCompare || ''
        const priceSavings = option.dataset.variantPriceSavings || ''
        const priceInstalment = option.dataset.variantPriceInstalment || ''

        // Update CSS variables
        priceElement.style.setProperty('--price-current', `'${priceCurrent}'`)
        priceElement.style.setProperty('--price-compare', `'${priceCompare}'`)
        priceElement.style.setProperty('--price-savings', `'${priceSavings}'`)
        priceElement.style.setProperty('--price-instalment', `'${priceInstalment}'`)

        // Update data-sale attribute
        const onSale = priceCompare !== '' && priceCompare !== priceCurrent
        priceElement.setAttribute('data-sale', onSale.toString())
      }
    }
  }

  private setupEventListeners(): void {
    // Handle option selection
    this.options.forEach(option => {
      option.addEventListener('click', (event) => this.handleOptionSelect(event))
    })

    // Handle programmatic changes to the select element
    if (this.select) {
      this.select.addEventListener('change', () => {
        if (this.select && this.select.value) {
          const selectedOption = this.container.querySelector<HTMLElement>(`[data-variant-id="${this.select.value}"]`)
          if (selectedOption) {
            this.updateSizeProperty(selectedOption)
          }
        }
      })
    }
  }

  private updatedSelectedState(option: HTMLElement): void {
    this.options.forEach(option => {
      option.classList.remove(...VariantSelector.SELECTED_BUTTON_CLASSES)
    })

    option.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
  }

  private handleOptionSelect(event: Event): void {
    const option = event.currentTarget as HTMLElement
    const variantId = option.dataset.variantId || ''
    const variantOption = option.dataset.variantOption || ''

    // Update the hidden select
    if (this.select) {
      this.select.value = variantId

      // Trigger change event for any listeners
      const changeEvent = new Event('change', { bubbles: true })
      this.select.dispatchEvent(changeEvent)
    }

    // Update price display
    this.updatePriceDisplay(option)

    // Update the visible stock indicator
    this.updateStockIndicator(option)

    // Update the sticky pair form
    if (this.pairFormId) {
      const pairedElement = document.getElementById(this.pairFormId)
      if (pairedElement) {
        const hiddenSelector = pairedElement.querySelector('variant-selector')
        if (hiddenSelector) {
          const matchingOption = hiddenSelector.querySelector<HTMLElement>(`[data-variant-id="${variantId}"]`)
          if (matchingOption) {
            matchingOption.click()
          }
        }
      }
    }

    // Update properties[Size] field for universal sizing
    this.updateSizeProperty(option)

    // Clear any other selected options
    this.updatedSelectedState(option)
    // Update the button text
    if (this.sizeLabel) {
      this.sizeLabel.textContent = variantOption

      if (this.dropdownButton) {
        this.dropdownButton.classList.add(...VariantSelector.SELECTED_BUTTON_CLASSES)
      }
    }

    if (this.urlSync) {
      // Update URL with variant
      window.history.replaceState({}, '', '?variant=' + variantId)
    }

    // Close the dropdown
    const closeButton = this.container.querySelector<HTMLElement>('[data-modal-role="close"]')
    if (closeButton) closeButton.click()
  }

  private updateStockIndicator(option: HTMLElement): void {
    const form = this.select?.closest('form')
    if (!form) return

    const target = form.querySelector<HTMLElement>('[data-stock-indicator-display]')
    if (!target) return

    target.innerHTML = option.dataset.stockIndicator || ''
  }

  private updateSizeProperty(option: HTMLElement): void {
    const form = this.select?.closest('form')
    if (!form) return

    const sizeMetricInput = form.querySelector('[data-quick-add-size-metric-input]') as HTMLInputElement
    if (!sizeMetricInput) return

    const hasUniversalSizing = option.hasAttribute('data-size-uk') ||
                                option.hasAttribute('data-size-us') ||
                                option.hasAttribute('data-size-eu') ||
                                option.hasAttribute('data-size-cm')

    if (!hasUniversalSizing) return

    const variantOption = option.dataset.variantOption || ''

    if (variantOption.startsWith('EU ')) {
      sizeMetricInput.value = variantOption.replace('EU ', 'EURO ')
    } else {
      sizeMetricInput.value = variantOption
    }
  }
}
